import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject
} from "react";
import type { AgGridReact } from "ag-grid-react";
import type {
  ExpandOrCollapseAllEvent,
  FirstDataRenderedEvent,
  GridApi,
  RowGroupOpenedEvent
} from "ag-grid-community";
import { anyGroupNodeExpanded } from "./ag-grid-tree-expand-state";

export type AgGridTreeExpandGridHandlers = {
  onRowGroupOpened: (event: RowGroupOpenedEvent) => void;
  onExpandOrCollapseAll: (event: ExpandOrCollapseAllEvent) => void;
  onFirstDataRendered: (event: FirstDataRenderedEvent) => void;
};

type UseAgGridTreeExpandToggleOptions = {
  gridRef: RefObject<AgGridReact | null>;
  /** When false, skips grid scans and leaves state at defaults (e.g. list view). */
  enabled: boolean;
  /**
   * Bumps when the tree remounts or the active hierarchy changes so the button
   * resets before the next grid sync (e.g. tree system or re-enter hierarchy).
   */
  resetKey?: string | number;
  /** Matches `groupDefaultExpanded={-1}` — all groups start expanded. */
  initiallyAllExpanded?: boolean;
};

/**
 * Single Expand all / Collapse all control for AG Grid treeData (or row groups).
 * Coalesces expansion scans to one frame per burst (cascade opens, bulk API).
 */
export function useAgGridTreeExpandToggle(
  options: UseAgGridTreeExpandToggleOptions
) {
  const { gridRef, enabled, resetKey, initiallyAllExpanded = true } = options;

  const [showCollapseAll, setShowCollapseAll] = useState(initiallyAllExpanded);
  const rafIdRef = useRef<number | null>(null);

  const syncFromApi = useCallback(
    (api: GridApi) => {
      if (!enabled || api.isDestroyed?.()) return;
      const next = anyGroupNodeExpanded(api);
      setShowCollapseAll((prev) => (prev === next ? prev : next));
    },
    [enabled]
  );

  const scheduleSyncFromApi = useCallback(
    (api: GridApi) => {
      if (!enabled || api.isDestroyed?.()) return;
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        // Grid may have remounted between schedule and frame (view/layout swap).
        if (api.isDestroyed?.()) return;
        syncFromApi(api);
      });
    },
    [enabled, syncFromApi]
  );

  useEffect(() => {
    if (!enabled) return;
    setShowCollapseAll(initiallyAllExpanded);
  }, [enabled, initiallyAllExpanded, resetKey]);

  // Drop a pending scan when the tree remounts or hierarchy mode turns off.
  useEffect(() => {
    if (rafIdRef.current != null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, [enabled, resetKey]);

  useEffect(
    () => () => {
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    },
    []
  );

  const onRowGroupOpened = useCallback(
    (event: RowGroupOpenedEvent) => {
      scheduleSyncFromApi(event.api);
    },
    [scheduleSyncFromApi]
  );

  const onExpandOrCollapseAll = useCallback(
    (event: ExpandOrCollapseAllEvent) => {
      if (!enabled) return;
      setShowCollapseAll(event.source === "expandAll");
    },
    [enabled]
  );

  const onFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent) => {
      scheduleSyncFromApi(event.api);
    },
    [scheduleSyncFromApi]
  );

  const toggleExpandCollapse = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api || api.isDestroyed?.()) return;
    if (showCollapseAll) {
      api.collapseAll();
    } else {
      api.expandAll();
    }
  }, [gridRef, showCollapseAll]);

  const gridHandlers: AgGridTreeExpandGridHandlers = {
    onRowGroupOpened,
    onExpandOrCollapseAll,
    onFirstDataRendered
  };

  return {
    /** When true, show Collapse all; otherwise Expand all. */
    showCollapseAll,
    toggleExpandCollapse,
    gridHandlers
  };
}
